import { normalizeOwnerEmail } from "@/lib/owner-metrics";
import { LEASE_END_WINDOW_DAYS } from "./thresholds";
import { daysUntil } from "./time";
import {
  keyPickupGaps,
  type KeyPickupGap,
  type PickupApartment,
  type PickupProperty,
  type PickupRoom,
} from "@/lib/key-pickup";
import type { BrainItem, BrainKind } from "./types";

export interface OwnerDeriveInput {
  /** The Owners list's rows (test owners already hidden). */
  owners: {
    id: string;
    name?: string | null;
    email?: string | null;
    is_platform?: boolean | null;
    bank_iban?: string | null;
    propertiesCount?: number;
    apartmentsCount?: number;
    /** Each property's own onboarding stage for this owner. */
    propertyList?: {
      id: number | string;
      title: string | null;
      onboardingStage: string | null;
      /** The owner is the property's own lessor (properties.lessor_id). */
      heldDirectly?: boolean;
    }[];
    /** The owner's apartments (effective lessor). */
    apartmentList?: {
      propertyId: number | string;
      /** Owned through the apartment itself, not inherited from the property. */
      apartmentScoped?: boolean;
    }[];
  }[];
  /** owner_onboarding_submissions rows. */
  submissions: {
    id: string;
    status: string;
    email?: string | null;
    name?: string | null;
    submitted_at: string | null;
  }[];
  /** Properties, any approval status; owner submissions carry submitted_by. */
  propertySubmissions: {
    id: number;
    title?: string | null;
    approval_status: string;
    submitted_by: string | null;
    submitted_at?: string | null;
  }[];
  leases: {
    scope: "property" | "apartment";
    id: number;
    propertyId: number;
    label: string | null;
    lease_end: string | null;
  }[];
  /** computeRepresentorGaps per property. */
  representorGaps: {
    propertyId: number;
    label: string | null;
    missingCount: number;
    pending: boolean;
    failed: boolean;
  }[];
  /** Live (approved) properties with their open units and rooms. */
  setup?: {
    properties: ({
      id: number | string;
      title?: string | null;
      lessor_id?: string | null;
    } & PickupProperty)[];
    apartments: ({
      id: number | string;
      property_id: number | string;
      label?: string | null;
      lessor_id?: string | null;
      payout_method?: string | null;
    } & PickupApartment)[];
    rooms: ({
      id: number | string;
      property_id: number | string;
      apartment_id?: number | string | null;
      /** rooms.lessor_override JSONB; .lessor_id is the room's own owner. */
      lessor_override?: { lessor_id?: string | null } | null;
      title?: string | null;
      price?: number | null;
      deposit_amount?: number | null;
    } & PickupRoom)[];
  };
}

const SETUP_NAMES = 5;
/** "A, B, C, D, E +3": a card fact, not a list. */
const names = (labels: string[]) =>
  labels.slice(0, SETUP_NAMES).join(", ") +
  (labels.length > SETUP_NAMES ? ` +${labels.length - SETUP_NAMES}` : "");

const ownerName = (o: OwnerDeriveInput["owners"][number]) =>
  o.name || o.email || o.id;

function item(
  kind: BrainKind,
  id: string,
  href: string,
  extra: Partial<BrainItem>
): BrainItem {
  return {
    key: `${kind}:${id}`,
    kind,
    since: null,
    facts: [],
    primaryHref: href,
    refs: {},
    ...extra,
  };
}

export function deriveOwnerItems(
  input: OwnerDeriveInput,
  now: Date
): BrainItem[] {
  const out: BrainItem[] = [];

  for (const s of input.submissions) {
    if (s.status !== "pending") continue;
    const href = `/admin?tab=owner-onboarding&submission=${s.id}`;
    const label = s.name || s.email;
    out.push(
      item("onboarding_review", s.id, href, {
        since: s.submitted_at,
        who: label ? { label, href } : undefined,
        refs: { email: s.email ?? null, personName: s.name ?? null },
      })
    );
  }

  // A pending submission is signed and waiting on our review: that owner's
  // card is onboarding_review, not an unsigned invite.
  const signedEmails = new Set(
    input.submissions
      .filter((s) => s.status === "pending")
      .map((s) => normalizeOwnerEmail(s.email))
      .filter((e) => e != null)
  );
  for (const o of input.owners) {
    const who = { label: ownerName(o), href: `/admin/owners?owner=${o.id}` };
    const refs = {
      lessorId: o.id,
      email: o.email ?? null,
      // The real name only: it greets the owner and prefills their invite.
      personName: o.name || null,
    };
    // Per property: the owner-level stage is the least advanced one, so a
    // never-invited property would hide an invited one.
    const invited = (o.propertyList ?? []).filter(
      (p) => p.onboardingStage === "invite_sent"
    );
    if (invited.length && !signedEmails.has(normalizeOwnerEmail(o.email))) {
      out.push(
        item("owner_invite_unsigned", o.id, who.href, {
          who,
          refs,
          facts: [
            {
              label: {
                key: "brain.fact.invitedProperties",
                fallback: "Invited to",
              },
              value: invited.map((p) => p.title || `#${p.id}`).join(", "),
            },
          ],
          invitedProperties: invited.map((p) => ({
            id: Number(p.id),
            title: p.title,
            // The card can only re-send a PROPERTY-WIDE invite, which onboards
            // (and has the owner sign the Vollmacht for) the whole building.
            // Safe only for the building's own lessor with no unit of theirs
            // owned apartment by apartment there; unknown counts as scoped.
            resendable:
              p.heldDirectly === true &&
              !(o.apartmentList ?? []).some(
                (a) =>
                  String(a.propertyId) === String(p.id) &&
                  a.apartmentScoped !== false
              ),
          })),
        })
      );
    }
    // CoLiviq's own entity collects the fees; it is never paid out.
    const units = (o.propertiesCount ?? 0) + (o.apartmentsCount ?? 0);
    if (!o.is_platform && units > 0 && !o.bank_iban?.trim()) {
      out.push(item("owner_bank_missing", o.id, who.href, { who, refs }));
    }
  }

  // Same set as the Properties pip (usePendingSubmissionCount). Approve and
  // reject live in that page's approvals rail, not on the property.
  for (const p of input.propertySubmissions) {
    if (p.approval_status !== "pending" || p.submitted_by == null) continue;
    out.push(
      item("property_submission", String(p.id), "/admin/properties", {
        since: p.submitted_at ?? null,
        where: {
          label: p.title || `#${p.id}`,
          href: `/admin/properties/${p.id}`,
        },
        refs: { propertyId: p.id },
      })
    );
  }

  for (const l of input.leases) {
    const d = daysUntil(l.lease_end, now);
    if (d === null || d < 0 || d > LEASE_END_WINDOW_DAYS) continue;
    const href = `/admin/properties/${l.propertyId}`;
    out.push(
      item("lease_end_approaching", `${l.scope}:${l.id}`, href, {
        where: { label: l.label || `#${l.id}`, href },
        facts: [
          {
            label: { key: "brain.fact.leaseEnd", fallback: "Contract ends" },
            date: l.lease_end ?? undefined,
          },
        ],
        refs: {
          propertyId: l.propertyId,
          apartmentId: l.scope === "apartment" ? l.id : null,
        },
      })
    );
  }

  for (const g of input.representorGaps) {
    if (g.pending || g.failed || g.missingCount === 0) continue;
    const href = `/admin/properties/${g.propertyId}`;
    out.push(
      item("representor_missing", String(g.propertyId), href, {
        where: { label: g.label || `#${g.propertyId}`, href },
        facts: [
          {
            label: {
              key: "brain.fact.apartments",
              fallback: "Apartments without one",
            },
            value: String(g.missingCount),
          },
        ],
        refs: { propertyId: g.propertyId },
      })
    );
  }

  // Owner, payout method, rent and deposit gaps, one card per building — but
  // when a building's gapped units belong to different owners, one card each,
  // so each owner's card can be assigned to them (COL-593). A room's
  // lessor_override is the most-specific owner (CLAUDE.md), so a unit whose
  // rooms each carry one is owned, not ownerless. ponytail: billing's
  // COALESCE(apartments.lessor_id, properties.lessor_id) still can't see a room
  // override, so an override-only unit counts as owned here yet is unbillable —
  // teach generate_management_invoices the override too if that gap bites.
  const setup = input.setup;
  if (setup) {
    const aptsBy = new Map<string, typeof setup.apartments>();
    for (const a of setup.apartments) {
      const k = String(a.property_id);
      aptsBy.set(k, [...(aptsBy.get(k) ?? []), a]);
    }
    const roomsBy = new Map<string, typeof setup.rooms>();
    for (const r of setup.rooms) {
      const k = String(r.property_id);
      roomsBy.set(k, [...(roomsBy.get(k) ?? []), r]);
    }
    for (const p of setup.properties) {
      const apts = aptsBy.get(String(p.id)) ?? [];
      const rooms = roomsBy.get(String(p.id)) ?? [];
      const title = p.title || `#${p.id}`;
      const href = `/admin/properties/${p.id}`;
      const aptName = (a: (typeof apts)[number]) => a.label || `#${a.id}`;
      const roomName = (r: (typeof rooms)[number]) => r.title || `#${r.id}`;
      const noPrice = (r: (typeof rooms)[number]) => !(Number(r.price) > 0);
      const noDeposit = (r: (typeof rooms)[number]) => r.deposit_amount == null;
      const aptOf = (r: (typeof rooms)[number]) =>
        apts.find((a) => String(a.id) === String(r.apartment_id)) ?? null;
      // Rooms missing a mandatory key-pickup detail, by detail (COL-607).
      const keyGapsOf = (rs: typeof rooms) => {
        const by: Record<KeyPickupGap, string[]> = {
          method: [],
          location: [],
          address: [],
          instructions: [],
          keynestLink: [],
          keynestCode: [],
        };
        for (const r of rs)
          for (const g of keyPickupGaps({
            room: r,
            apartment: aptOf(r),
            property: p,
          }))
            by[g].push(roomName(r));
        return by;
      };
      const factsOf = (
        owner: string[],
        payout: string[],
        rent: string[],
        deposit: string[],
        keys: ReturnType<typeof keyGapsOf>
      ) =>
        (
          [
            ["brain.fact.ownerMissing", "Owner missing", owner],
            ["brain.fact.payoutMissing", "Payout method missing", payout],
            ["brain.fact.rentMissing", "Rent missing", rent],
            ["brain.fact.depositMissing", "Deposit missing", deposit],
            [
              "brain.fact.pickupMethodMissing",
              "Pickup method missing",
              keys.method,
            ],
            [
              "brain.fact.pickupLocationMissing",
              "Pickup location missing",
              keys.location,
            ],
            [
              "brain.fact.pickupAddressMissing",
              "Pickup address missing",
              keys.address,
            ],
            [
              "brain.fact.pickupInstructionsMissing",
              "Pickup instructions missing",
              keys.instructions,
            ],
            [
              "brain.fact.keynestLinkMissing",
              "KeyNest link missing",
              keys.keynestLink,
            ],
            [
              "brain.fact.keynestCodeMissing",
              "KeyNest pickup code missing",
              keys.keynestCode,
            ],
          ] as [string, string, string[]][]
        )
          .filter(([, , list]) => list.length)
          .map(([key, fallback, list]) => ({
            label: { key, fallback },
            value: names(list),
          }));
      const emit = (
        id: string,
        label: string,
        facts: ReturnType<typeof factsOf>,
        apartmentId: number | null
      ) => {
        if (!facts.length) return;
        out.push(
          item("property_setup_missing", id, href, {
            where: { label, href },
            facts,
            refs: { propertyId: Number(p.id), apartmentId },
          })
        );
      };

      const roomsOf = (a: (typeof apts)[number]) =>
        rooms.filter((r) => String(r.apartment_id) === String(a.id));
      // A room carries its own owner via lessor_override (CLAUDE.md's
      // most-specific level); null when it doesn't.
      const roomOwner = (r: (typeof rooms)[number]) =>
        r.lessor_override?.lessor_id ?? null;
      // Ownerless only when the unit has no apt/property lessor AND not every
      // one of its rooms is owned through a room override.
      const aptOwnerless = (a: (typeof apts)[number]) => {
        if (p.lessor_id || a.lessor_id) return false;
        const rs = roomsOf(a);
        return !(rs.length > 0 && rs.every(roomOwner));
      };
      // A gapped unit's effective owner, to group cards by owner: its own
      // lessor, else the building's (billing's COALESCE(apartments.lessor_id,
      // properties.lessor_id)), else a single room-override owner shared by all
      // its rooms. "" is the ownerless bucket — a distinct owner to be assigned.
      const ownerOf = (a: (typeof apts)[number]) => {
        if (a.lessor_id) return a.lessor_id;
        if (p.lessor_id) return p.lessor_id;
        const ids = new Set(roomsOf(a).map(roomOwner));
        return (ids.size === 1 ? [...ids][0] : null) ?? "";
      };
      const aptFacts = (a: (typeof apts)[number]) =>
        factsOf(
          aptOwnerless(a) ? [aptName(a)] : [],
          a.payout_method ? [] : [aptName(a)],
          roomsOf(a).filter(noPrice).map(roomName),
          roomsOf(a).filter(noDeposit).map(roomName),
          keyGapsOf(roomsOf(a))
        );
      const gapped = apts.filter((a) => aptFacts(a).length);
      const manyOwners = new Set(gapped.map(ownerOf)).size > 1;

      if (manyOwners) {
        for (const a of gapped)
          emit(
            `${p.id}:apt:${a.id}`,
            `${title} · ${aptName(a)}`,
            aptFacts(a),
            Number(a.id)
          );
        // Rooms on no apartment belong to the building, not any one owner.
        const orphan = rooms.filter(
          (r) => !apts.some((a) => String(a.id) === String(r.apartment_id))
        );
        emit(
          String(p.id),
          title,
          factsOf(
            [],
            [],
            orphan.filter(noPrice).map(roomName),
            orphan.filter(noDeposit).map(roomName),
            keyGapsOf(orphan)
          ),
          null
        );
        continue;
      }

      // One card for the whole building (the common case).
      const noOwner = p.lessor_id
        ? []
        : apts.length
          ? apts.filter(aptOwnerless).map(aptName)
          : rooms.length && rooms.every(roomOwner)
            ? []
            : [title];
      emit(
        String(p.id),
        title,
        factsOf(
          noOwner,
          apts.filter((a) => !a.payout_method).map(aptName),
          rooms.filter(noPrice).map(roomName),
          rooms.filter(noDeposit).map(roomName),
          keyGapsOf(rooms)
        ),
        null
      );
    }
  }
  return out;
}
